import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Tilt3D from "./Tilt3D";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

function diff(target) {
  const ms = Math.max(0, new Date(target).getTime() - Date.now());
  return {
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms / 3600000) % 24),
    m: Math.floor((ms / 60000) % 60),
    s: Math.floor((ms / 1000) % 60),
    live: ms === 0,
  };
}

function Digit({ value, label }) {
  return (
    <div className="text-center" style={{ perspective: 400 }}>
      <div className="relative h-14 md:h-20 overflow-hidden">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={value}
            initial={{ rotateX: -90, opacity: 0, y: 20 }}
            animate={{ rotateX: 0, opacity: 1, y: 0 }}
            exit={{ rotateX: 90, opacity: 0, y: -20 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex items-center justify-center font-serif text-5xl md:text-7xl text-gold-light tabular-nums"
          >
            {value}
          </motion.span>
        </AnimatePresence>
      </div>
      <p className="mt-1 text-[9px] tracking-[0.35em] uppercase text-ivory/50">{label}</p>
    </div>
  );
}

export default function PriveLaunch({ compact = false }) {
  const { user } = useAuth();
  const [launch, setLaunch] = useState(null);
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/prive/launch").then((r) => setLaunch(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!launch) return undefined;
    setT(diff(launch.launch_at));
    const id = setInterval(() => setT(diff(launch.launch_at)), 1000);
    return () => clearInterval(id);
  }, [launch]);

  if (!launch?.product || !t) return null;
  const member = Boolean(user?.prive);
  const p = launch.product;

  const reserve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prive/reserve");
      setLaunch((l) => ({ ...l, reserved: true, reservation_no: data.reservation_no, reserved_count: data.reserved_count }));
      toast.success(`Bottle No. ${data.reservation_no} of ${data.edition} is reserved for you`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="prive-launch"
      className={`relative overflow-hidden bg-ink text-ivory border border-gold/30 gold-glow-hover ${compact ? "" : "mx-6 lg:mx-12 my-16 md:my-20 shadow-[0_40px_120px_-40px_rgba(212,175,55,0.45)]"}`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(212,175,55,0.22),transparent_55%)]" />
      <img src="/brand/logo-seal.png" alt="" aria-hidden="true" className="absolute -left-20 -bottom-24 w-[26rem] opacity-[0.07] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 items-center p-7 md:p-12 lg:p-16">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <Crown size={15} className="text-gold-light" />
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold-light">ATW Privé · Next release · by Dewan Ali</p>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95] mt-4">
            {p.name.split(" ").slice(0, -1).join(" ")} <span className="italic text-gold-light">{p.name.split(" ").slice(-1)}</span>
          </h2>
          <p className="mt-5 text-ivory/60 leading-relaxed max-w-lg">{p.description}</p>

          <div data-testid="prive-countdown" className="mt-9 grid grid-cols-4 gap-3 md:gap-6 max-w-lg">
            <Digit value={pad(t.d)} label="Days" />
            <Digit value={pad(t.h)} label="Hours" />
            <Digit value={pad(t.m)} label="Minutes" />
            <Digit value={pad(t.s)} label="Seconds" />
          </div>
          <p className="mt-3 text-[10px] tracking-[0.3em] uppercase text-ivory/40">
            {t.live ? "Now available to members" : `Releases ${new Date(launch.launch_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · Edition of ${launch.edition} · ${launch.reserved_count} reserved`}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            {member ? (
              launch.reserved ? (
                <span data-testid="prive-reserved-badge" className="flex items-center gap-2 border border-gold-light text-gold-light px-6 py-4 text-[11px] tracking-[0.25em] uppercase">
                  <Crown size={14} /> Bottle No. {launch.reservation_no} reserved for you
                </span>
              ) : (
                <button
                  data-testid="prive-reserve-button"
                  onClick={reserve}
                  disabled={busy}
                  className="bg-gold-light text-ink px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-ivory transition-colors disabled:opacity-50"
                >
                  {busy ? "Reserving…" : "Reserve my bottle"}
                </button>
              )
            ) : (
              <Link
                to={user ? "/account" : "/auth"}
                data-testid="prive-launch-join"
                className="flex items-center gap-2 bg-gold-light text-ink px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-ivory transition-colors"
              >
                <Lock size={13} /> {user ? "Enter invitation code" : "Members only — join Privé"}
              </Link>
            )}
            <Link to={`/product/${p.id}`} data-testid="prive-launch-product" className="text-[11px] tracking-[0.25em] uppercase text-ivory/70 hover:text-gold-light transition-colors">
              View the edition
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <Tilt3D max={8} className="aspect-[3/4] max-w-sm mx-auto overflow-hidden border border-gold/30">
            <img src={p.image} alt={p.name} loading="lazy" className={`h-full w-full object-cover ${member ? "" : "grayscale-[0.3]"}`} />
            {!member && (
              <div className="absolute inset-0 flex items-center justify-center bg-ink/30">
                <span className="w-14 h-14 rounded-full bg-ink/70 border border-gold/60 flex items-center justify-center"><Lock size={18} className="text-gold-light" /></span>
              </div>
            )}
          </Tilt3D>
        </div>
      </div>
    </section>
  );
}
